import { alpha, Box, TableCell, TableRow, useTheme } from "@mui/material"
import { htmlToText } from "lib/html-to-text"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { createSlot } from "lib/@components/slot/create-slot"
import { Bound } from "lib/@components/binding/Bound"
import { navigate } from "lib/routes/navigate"
import { getMappingStatus } from "./plugins/asset-mapping/controller/get-mapping-status"
import { refreshId } from "lib/graphql/api"
import { statsForRegister } from "./plugins/asset-mapping/controller/stats-for-register"
import { RegisterItem } from "./register-item"
import { DATE_FORMATS } from "lib/@constants/date-format"

export const RegisterItemEnd = createSlot("RegisterItemEnd")

export function Register({ item }) {
    const theme = useTheme()
    const { mappingKeys } = getMappingStatus.useResults(refreshId(item?.id)) ?? {}
    const { mapped } = statsForRegister.useResults(refreshId(item?.id)) ?? {}
    const status = getRegisterStatus()
    const statusStyle = getStatusStyle()

    return (
        <Bound register={item}>
            <TableRow
                hover
                tabIndex={0}
                role="button"
                aria-label={`Open register ${item.name}`}
                data-cy="register-list-item-row"
                onClick={navigateToRegister}
                onKeyDown={handleRowKeyDown}
                sx={{
                    cursor: "pointer",
                    "&:focus-visible": {
                        outline: "none",
                        bgcolor: "action.hover",
                    },
                }}
            >
                <TableCell sx={{ maxWidth: { xs: 180, md: 320 } }}>
                    <Box
                        data-cy="register-list-item"
                        sx={{
                            alignItems: "flex-start",
                            display: "flex",
                            flexDirection: "column",
                            minWidth: 0,
                            width: "100%",
                        }}
                    >
                        <TruncatedTypography
                            noWrap
                            data-cy="asset-register-name"
                            sx={{ display: "block", maxWidth: "100%" }}
                        >
                            {item.name}
                        </TruncatedTypography>
                        <TruncatedTypography
                            noWrap
                            data-cy="asset-register-description"
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", maxWidth: "100%" }}
                        >
                            {htmlToText(item.description)}
                        </TruncatedTypography>
                    </Box>
                </TableCell>
                <TableCell data-cy="register-regime-cell-content">
                    <RegisterItem.Slot />
                </TableCell>
                <TableCell data-cy="register-assets-count-cell-content">{item.assetCount}</TableCell>
                <TableCell data-cy="register-status-cell-content">
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxSizing: "border-box",
                            px: 1.2,
                            py: 0.8,
                            borderRadius: 1,
                            fontSize: 14,
                            fontWeight: 600,
                            lineHeight: 1,
                            textAlign: "center",
                            ...statusStyle,
                        }}
                    >
                        {status}
                    </Box>
                </TableCell>
                <TableCell data-cy="asset-register-modified-date">
                    {Date.create(item.modified).format(DATE_FORMATS.long)}
                </TableCell>
                <TableCell>
                    <RegisterItemEnd.Slot />
                </TableCell>
            </TableRow>
        </Bound>
    )

    function navigateToRegister() {
        navigate(`/app/assets/${item.id}`)
    }

    function handleRowKeyDown(e) {
        if (e.target !== e.currentTarget) return
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            navigateToRegister()
        }
    }

    function getRegisterStatus() {
        if (item?.assetCount === 0) {
            return "Created"
        }
        if ((mappingKeys?.length ?? 0) > 0) {
            return "Mapping"
        }
        if ((mappingKeys?.length ?? 0) === 0 && mapped > 0) {
            return "Mapped"
        }
        if (item?.assetCount > 0 && !mapped) {
            return "Imported"
        }
        return "Created"
    }

    function getStatusStyle() {
        switch (status) {
            case "Imported":
                return {
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                }
            case "Mapping":
                return {
                    color: "warning.darker",
                    backgroundColor: "warning.lighter",
                }
            case "Mapped":
                return {
                    color: "success.darker",
                    backgroundColor: "success.lighter",
                }
            default:
                return {
                    color: theme.palette.grey[700],
                    backgroundColor: theme.palette.grey[500_16] ?? theme.palette.grey[300],
                }
        }
    }
}
